import { useMemo } from 'react';

import { useMount } from 'react-use';

import groupBy from 'lodash/groupBy';

import { ProductCategory, UsageType } from '@/graphql';
import { namedOperations as monetaryCreditInfoNamedOperations } from '@/hooks/useMonetaryCreditInfo/__generated__/queries.generated';
import useOrg from '@/hooks/useOrg';
import {
  useSelectedBillingPeriodIndex,
  useUsageForBillingPeriodModelServing,
} from '@/pages/Settings/Tabs/Billing/UsageSection/hooks';
import { calcTotalValuesForSharedEndpoints } from '@/pages/Settings/Tabs/Billing/UsageSection/utils';
import { useSearchParams } from '@/routes/hooks';
import { namedOperations as paymentOverdueNamedOperations } from '@/sections/GlobalHeader/Right/LoggedIn/PaymentOverdue/__generated__/queries.generated';
import { formatPrice } from '@/utils/currency';
import { getStripePromise } from '@/utils/stripe';
import { useSnackbar } from 'notistack';

import {
  useGenerateStripeSetupIntentMutation,
  useSetPaymentMethodMutation,
} from './__generated__/mutations.generated';
import {
  PaymentMethodDocument,
  namedOperations as billingNamedOperations,
  useAvailableBillingPeriodsQuery,
  useBillingThresholdQuery,
  useCanUpdatePaymentMethodQuery,
  useInvoicesQuery,
  useOrgBudgetQuery,
  useOrgSharedEndpointsUsageQuery,
  usePaymentMethodQuery,
  useTrainingUsageForPeriodQuery,
  useUsageForBillingPeriodQuery,
  useUsageSummaryForDateRangeQuery,
} from './__generated__/queries.generated';
import { TrainingProjectUsageEntry } from './types';

function useGenerateStripeSetupIntent() {
  const { enqueueSnackbar } = useSnackbar();

  return useGenerateStripeSetupIntentMutation({
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });
}

function usePaymentMethod() {
  const { data, loading } = usePaymentMethodQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    paymentMethod: data?.stripePaymentMethod,
    isLoading: loading,
  };
}

function useSetPaymentMethod() {
  return useSetPaymentMethodMutation({
    update: (cache, { data }) => {
      if (data) {
        cache.writeQuery({
          query: PaymentMethodDocument,
          data: {
            stripePaymentMethod: data.setStripeDefaultPaymentMethod.paymentMethod,
          },
        });
      }
    },
  });
}

function useUsageForBillingPeriodCategory(
  category: ProductCategory,
  billingPeriodIndex: number = 0,
) {
  const org = useOrg();
  const isOrbBillingEnabled = org.orbMigrationCompletedAt;
  const { availableBillingPeriods } = useAvailableBillingPeriods();
  const billingPeriod = availableBillingPeriods[billingPeriodIndex];

  // Call both hooks unconditionally, but skip based on orb migration status
  const { data: oldData, loading: oldLoading } = useUsageForBillingPeriodQuery({
    fetchPolicy: 'cache-first',
    variables: {
      billingPeriodIndex,
      timezoneName: 'UTC',
    },
    skip: isOrbBillingEnabled, // Skip old query if orb migration completed
    notifyOnNetworkStatusChange: true,
  });

  const { dedicatedUsage, isLoading: newLoading } = useUsageSummaryForDateRange(
    billingPeriod?.start,
    billingPeriod?.end,
    [UsageType.Dedicated],
    { skip: !isOrbBillingEnabled || !billingPeriod },
  );

  // Use new data if orb migration completed, otherwise fall back to old
  const data = isOrbBillingEnabled
    ? dedicatedUsage?.productCategoryUsages?.find((option) => option.category === category)
    : oldData?.usageForBillingPeriod?.productCategoryUsages?.find(
        (option) => option.category === category,
      );

  const loading = isOrbBillingEnabled ? newLoading : oldLoading;

  // Handle billing period - need to construct from date range if using new query
  const billingPeriodData = isOrbBillingEnabled
    ? billingPeriod
      ? {
          start: billingPeriod.start,
          end: billingPeriod.end,
          index: billingPeriodIndex,
          isCurrent: billingPeriod.isCurrent,
        }
      : undefined
    : oldData?.usageForBillingPeriod?.billingPeriod;

  return {
    isLoading: loading,
    data,
    billingPeriod: billingPeriodData,
    currentPeriodTotal: isOrbBillingEnabled
      ? dedicatedUsage?.currentPeriodTotal
      : oldData?.usageForBillingPeriod?.currentPeriodTotal,
    monetaryCreditUsage: isOrbBillingEnabled
      ? dedicatedUsage?.monetaryCreditUsage
      : oldData?.usageForBillingPeriod?.monetaryCreditUsage,
  };
}

function useAvailableBillingPeriods() {
  const { data, loading } = useAvailableBillingPeriodsQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    isLoading: loading,
    availableBillingPeriods: [...(data?.availableBillingPeriods || [])].sort(
      (a, b) => Number(b.isCurrent) - Number(a.isCurrent),
    ),
  };
}

function useOrgSharedEndpointsUsage() {
  const org = useOrg();
  const isOrbBillingEnabled = org.orbMigrationCompletedAt;
  const { availableBillingPeriods } = useAvailableBillingPeriods();
  const [selectedBillingPeriodIndex] = useSelectedBillingPeriodIndex();
  const billingPeriod = availableBillingPeriods[selectedBillingPeriodIndex];

  // Call both hooks unconditionally
  const { data: oldData, loading: oldLoading } = useOrgSharedEndpointsUsageQuery({
    fetchPolicy: 'cache-first',
    variables: {
      startDate: billingPeriod?.start,
      endDate: billingPeriod?.end,
    },
    skip: !billingPeriod || isOrbBillingEnabled, // Skip old query if orb migration completed
  });

  const { sharedEndpointsUsage, isLoading: newLoading } = useUsageSummaryForDateRange(
    billingPeriod?.start,
    billingPeriod?.end,
    [UsageType.SharedEndpoints],
    { skip: !isOrbBillingEnabled || !billingPeriod },
  );

  // Use new data if orb migration completed, otherwise fall back to old
  const usageData = isOrbBillingEnabled
    ? sharedEndpointsUsage
    : oldData?.orgSharedEndpointsUsageForDateRange;

  const { totalCost } = useMemo(
    () => calcTotalValuesForSharedEndpoints(usageData?.usages),
    [usageData?.usages],
  );

  const usagePerSharedEndpoint = useMemo(() => {
    return groupBy(usageData?.usages || [], 'orgSharedEndpointId');
  }, [usageData]);

  return {
    isLoading: isOrbBillingEnabled ? newLoading : oldLoading,
    data: usageData,
    totalCost,
    creditsUsed: Number(usageData?.creditsUsed || 0),
    billingPeriod,
    usagePerSharedEndpoint,
  };
}

function useTrainingUsage(trainingProjectId?: string) {
  const org = useOrg();
  const isOrbBillingEnabled = org.orbMigrationCompletedAt;
  const [billingPeriodIndex] = useSelectedBillingPeriodIndex();
  const { availableBillingPeriods } = useAvailableBillingPeriods();
  const billingPeriod = availableBillingPeriods[billingPeriodIndex];

  // Call both hooks unconditionally
  const { data: oldData, loading: oldLoading } = useTrainingUsageForPeriodQuery({
    fetchPolicy: 'cache-first',
    variables: {
      billingPeriodIndex,
      trainingProjectId,
      timezoneName: 'UTC',
    },
    skip: isOrbBillingEnabled, // Skip old query if orb migration completed
    notifyOnNetworkStatusChange: true,
  });

  const { trainingUsage, isLoading: newLoading } = useUsageSummaryForDateRange(
    billingPeriod?.start,
    billingPeriod?.end,
    [UsageType.Training],
    { skip: !isOrbBillingEnabled || !billingPeriod },
  );

  // Use new data if orb migration completed, otherwise fall back to old
  const trainingUsageForPeriod = isOrbBillingEnabled
    ? trainingUsage
    : oldData?.trainingUsageForPeriod;

  const totalCostDollars = trainingUsageForPeriod?.cost ? Number(trainingUsageForPeriod.cost) : 0;
  const totalMinutes = trainingUsageForPeriod?.minutes ? Number(trainingUsageForPeriod.minutes) : 0;

  const projectUsageById = useMemo(() => {
    const projectUsageById: Record<string, TrainingProjectUsageEntry> = {};

    (trainingUsageForPeriod?.jobBreakdown || []).forEach(
      ({ minutes, cost, billingEntity: { parentId, parentName, id, name }, daily }) => {
        if (!(parentId in projectUsageById)) {
          projectUsageById[parentId] = { name: parentName, minutes: 0, cost: 0, jobs: [] };
        }

        projectUsageById[parentId].minutes += Number(minutes);
        projectUsageById[parentId].cost += Number(cost);

        projectUsageById[parentId].jobs.push({
          daily: daily.map(({ minutes, cost, date }) => ({
            minutes: Number(minutes),
            cost: Number(cost),
            date,
          })),
          minutes: Number(minutes),
          cost: Number(cost),
          name: name || id,
          id,
        });
      },
    );

    return projectUsageById;
  }, [trainingUsageForPeriod]);

  // Handle billing period - need to construct from date range if using new query
  const billingPeriodData = isOrbBillingEnabled
    ? billingPeriod
      ? {
          start: billingPeriod.start,
          end: billingPeriod.end,
          index: billingPeriodIndex,
          isCurrent: billingPeriod.isCurrent,
        }
      : undefined
    : oldData?.trainingUsageForPeriod?.billingPeriod;

  return {
    monetaryCreditUsageCents: trainingUsageForPeriod?.monetaryCreditUsage
      ? Number(trainingUsageForPeriod.monetaryCreditUsage)
      : 0,
    billingPeriod: billingPeriodData,
    isLoading: isOrbBillingEnabled ? newLoading : oldLoading,
    projectUsageById,
    totalCostDollars,
    totalMinutes,
    jobSubsetCost: trainingUsageForPeriod?.jobSubsetCost,
  };
}

function useGrandTotalCost() {
  // Use the three existing hooks that already handle orb migration logic internally
  const {
    currentPeriodTotal: currentPeriodDedicatedCostDollars,
    monetaryCreditUsage: currentPeriodDedicatedCreditsUsedCents,
    isLoading: isLoadingDedicatedUsage,
  } = useUsageForBillingPeriodModelServing();
  const {
    totalCost: currentPeriodModelApisCostDollars,
    creditsUsed: currentPeriodModelApisCreditsUsedDollars,
    isLoading: isLoadingModelApisUsage,
  } = useOrgSharedEndpointsUsage();
  const {
    totalCostDollars: currentPeriodTrainingCostDollars,
    monetaryCreditUsageCents: currentPeriodTrainingCreditsUsedCents,
    isLoading: isLoadingTrainingUsage,
  } = useTrainingUsage();

  const dedicatedCostDollars = parseFloat(currentPeriodDedicatedCostDollars || '0');
  const dedicatedCreditsCents = currentPeriodDedicatedCreditsUsedCents;

  const trainingCostDollars = currentPeriodTrainingCostDollars;
  const trainingCreditsCents = currentPeriodTrainingCreditsUsedCents;

  const sharedEndpointsCostDollars = currentPeriodModelApisCostDollars;
  const sharedEndpointsCreditsDollars = currentPeriodModelApisCreditsUsedDollars;

  const loading = isLoadingDedicatedUsage || isLoadingModelApisUsage || isLoadingTrainingUsage;

  const grandTotalCostCents = loading
    ? 0
    : (dedicatedCostDollars + sharedEndpointsCostDollars + trainingCostDollars) * 100;
  const grandTotalCreditsUsedCents = loading
    ? 0
    : dedicatedCreditsCents + trainingCreditsCents + sharedEndpointsCreditsDollars * 100;

  const total = Math.max(grandTotalCostCents - grandTotalCreditsUsedCents, 0);

  return {
    formattedTotal: formatPrice(total),
    total,
    grandTotalCostCents,
    grandTotalCreditsUsedCents,
    loading,
  };
}

function useInvoices() {
  const { data, loading } = useInvoicesQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    invoices: data?.invoices,
    isLoading: loading,
  };
}

function useClientSecretFromSearchParams() {
  const [searchParams] = useSearchParams();
  return searchParams.get('setup_intent_client_secret');
}

function useHandleSetupIntentCallback() {
  const [setPaymentMethod] = useSetPaymentMethod();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const clientSecretFromSearchParams = useClientSecretFromSearchParams();

  useMount(() => {
    (async () => {
      if (clientSecretFromSearchParams) {
        // Remove the client secret from the URL
        window.history.replaceState({}, document.title, window.location.pathname);

        const stripe = await getStripePromise();

        const loadingKey = enqueueSnackbar('Saving payment method...', {
          variant: 'default',
          persist: true,
        });

        const { setupIntent } = await stripe.retrieveSetupIntent(clientSecretFromSearchParams);
        // Inspect the SetupIntent `status` to indicate the status of the payment
        // to your customer.
        //
        // Some payment methods will [immediately succeed or fail][0] upon
        // confirmation, while others will first enter a `processing` state.
        //
        // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
        switch (setupIntent.status) {
          case 'succeeded':
            try {
              const { data } = await setPaymentMethod({
                variables: {
                  id: setupIntent.payment_method as string,
                },
                refetchQueries: [
                  monetaryCreditInfoNamedOperations.Query.MonetaryCredits,
                  billingNamedOperations.Query.PaymentMethod,
                  billingNamedOperations.Query.Invoices,
                  paymentOverdueNamedOperations.Query.PaymentOverdue,
                ],
              });

              if (data?.setStripeDefaultPaymentMethod?.allInvoicesPaid === false) {
                const errorMessage = data.setStripeDefaultPaymentMethod.paymentAttemptErrorMessage;
                enqueueSnackbar(
                  [
                    'Your payment method has been saved but some invoices failed to process.',
                    errorMessage,
                  ]
                    .filter(Boolean)
                    .join(' '),
                  {
                    variant: 'warning',
                  },
                );
              } else {
                enqueueSnackbar('Success! Your payment method has been saved.', {
                  variant: 'success',
                });
              }
            } catch (error) {
              enqueueSnackbar('Failed to save payment method.', {
                variant: 'error',
              });
            }
            break;

          case 'processing':
            enqueueSnackbar(
              "Processing payment details. We'll update you when processing is complete.",
              {
                variant: 'info',
              },
            );
            break;

          case 'requires_payment_method':
            // Redirect your user back to your payment page to attempt collecting
            // payment again
            enqueueSnackbar(
              'Failed to process payment details. Please try another payment method.',
              {
                variant: 'error',
              },
            );
            break;
        }

        closeSnackbar(loadingKey);
      }
    })();
  });
}

function useBillingThreshold() {
  return useBillingThresholdQuery({
    fetchPolicy: 'cache-first',
  });
}

function useOrgBudget() {
  const org = useOrg();
  const isOrbBillingEnabled = org.orbMigrationCompletedAt;
  const { data, loading } = useOrgBudgetQuery({
    fetchPolicy: 'cache-first',
  });

  const orgData = data?.organization;

  const orgBudget = orgData?.budget ? Number(orgData?.budget) : 0;
  const orgSharedEndpointsNetSpend = orgData?.sharedEndpointsNetSpend
    ? Number(orgData.sharedEndpointsNetSpend)
    : 0;
  const currentNetSpend = orgData?.currentNetSpend?.netSpendDollars
    ? Number(orgData.currentNetSpend.netSpendDollars)
    : 0;

  // Use currentNetSpend for budget comparison when Orb billing is enabled, otherwise use orgSharedEndpointsNetSpend
  const netSpendForBudgetComparison = isOrbBillingEnabled
    ? currentNetSpend
    : orgSharedEndpointsNetSpend;

  return {
    orgBudget,
    orgSharedEndpointsNetSpend,
    currentNetSpend,
    shouldDisableSharedEndpointsOnBudgetReached:
      orgData?.shouldDisableSharedEndpointsOnBudgetReached,
    isSpendExceeded: !loading && !!orgBudget && netSpendForBudgetComparison > orgBudget,
    isLoading: loading,
  };
}

function useCanUpdatePaymentMethod() {
  const { data, loading } = useCanUpdatePaymentMethodQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    canUpdatePaymentMethod: data?.organization?.canUpdatePaymentMethod,
    isLoading: loading,
  };
}

/**
 * Hook to fetch usage summary for a date range.
 * This query requires orb migration to be completed (orb_migration_completed_at is set).
 * The backend will reject the query if orb migration is not completed.
 *
 * @param startDate - Start date of the range
 * @param endDate - End date of the range
 * @param usageTypes - Optional array of usage types to fetch. If not provided, fetches all types.
 * @param options - Optional query options (skip, etc.)
 */
function useUsageSummaryForDateRange(
  startDate: Date,
  endDate: Date,
  usageTypes?: UsageType[],
  options?: { skip?: boolean },
) {
  const org = useOrg();
  const isOrbBillingEnabled = org.orbMigrationCompletedAt;

  const { data, loading, error } = useUsageSummaryForDateRangeQuery({
    fetchPolicy: 'cache-first',
    variables: {
      startDate,
      endDate,
      usageTypes: usageTypes || [
        UsageType.Dedicated,
        UsageType.Training,
        UsageType.SharedEndpoints,
      ],
      timezoneName: 'UTC',
    },
    skip: options?.skip || !isOrbBillingEnabled, // Skip query if orb migration is not completed
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all', // Return partial data even if there are errors
  });

  // Check if error is due to orb migration not being completed
  const isOrbBillingError =
    error?.message?.includes('Orb billing must be enabled') ||
    error?.message?.includes('orb billing') ||
    error?.message?.includes('orb migration');

  return {
    isLoading: loading,
    data: data?.usageSummaryForDateRange,
    dedicatedUsage: data?.usageSummaryForDateRange?.dedicatedUsage,
    trainingUsage: data?.usageSummaryForDateRange?.trainingUsage,
    sharedEndpointsUsage: data?.usageSummaryForDateRange?.sharedEndpointsUsage,
    error,
    isOrbBillingError,
    // Orb migration completion is required for this query. If not completed, the backend will reject it.
    isOrbBillingEnabled: isOrbBillingEnabled && !isOrbBillingError,
  };
}

export {
  useBillingThreshold,
  useGenerateStripeSetupIntent,
  useHandleSetupIntentCallback,
  useInvoices,
  usePaymentMethod,
  useSetPaymentMethod,
  useAvailableBillingPeriods,
  useUsageForBillingPeriodCategory,
  useCanUpdatePaymentMethod,
  useOrgSharedEndpointsUsage,
  useOrgBudget,
  useTrainingUsage,
  useGrandTotalCost,
  useUsageSummaryForDateRange,
};
